import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDocumentTitle } from '../../../hooks/use-document-title';
import { useExpectedToday } from '../../../hooks/use-expected-today';
import { useExpectedStays } from '../../../hooks/use-expected-stays';
import {
  ContentAndButtonsContainer,
  NavbarAndContentContainer,
} from '../../../layout/layout-container.styled';
import { ScreenHeader } from '../../../layout/page-header';
import { Presence, Staleable, StateSwitch } from '../../../../motion';
import {
  ListSkeleton,
  LoadError,
} from '../../dashboard/dashboard-shared-components/load-state';
import { DefaultComponentProps } from '../../../app-router/component-props';
import { ScanIdButton } from '../../../capture/scan-id-button';
import { useAuth } from '../../../../context/auth-provider';
import { can, PERMISSIONS } from '../../../../auth/permissions';
import { useShifts } from '../../../shifts/shift-context';
import toast from '../../../../utils/toast';
import {
  SignInFromPassError,
  SignInFromPassResult,
  resolvePassLink,
  signInFromPass,
  signInFromPassToken,
} from '../../../../api/visitor-passes-client';
import { tokenFromScan } from '../../../../utils/pass-link';
import {
  VisitorExitError,
  signVisitorOut,
} from '../../../../api/visitor-log-client';
import { ExpectedRow } from '../../../../interfaces/visitor-pass';
import { ExpectedStayRow } from '../../../../interfaces/stays';
import { SignInFromStayError, signInFromStay } from '../../../../api/stays-client';
import {
  Banner,
  Card,
  CardTitle,
  Empty,
  Hint,
  Input,
  Label,
  RowAction,
  ScanRow,
  SearchRow,
  Stat,
  Stats,
  bannerMotion,
} from '../../visitor-exit/visitor-exit-page.styled';
import { ExpectedBody, ListHead, ZoneNote } from './expected-page.styled';
import { DeskPassesModal } from './desk-passes-modal';
import { PassDetailModal } from '../../../pass-editor/pass-detail-modal';
import { AppButton } from '../../../button/app-button';
import { clockNote } from '../../../../function-utils/building-time';
import { useClock } from '../../../../context/clock-context';
import {
  ConfirmAct,
  ExpectedRowItem,
  RowInlineError,
  SignInChoice,
} from './expected-row';
import {
  ExpectedScanOutcome,
  deskDateLabel,
  filterExpected,
  firstNameOf,
  fullNameOf,
  isSignable,
  matchScannedPass,
  rowDomId,
  rowKey,
  sortExpected,
} from './expected-model';
import { ExpectedStayRowItem, StaySignInChoice } from './expected-stay-row';
import {
  filterExpectedStays,
  matchScannedStays,
  sortExpectedStays,
  stayDomId,
  stayFirstName,
  stayFullName,
  stayKey,
  staySignInGate,
  staysLaneVisible,
} from './expected-stays-model';

// ---------------------------------------------------------------------------
// EXPECTED TODAY — the desk's list of people with a visitor pass for today,
// and the one tap that signs one of them in (resident-portal Plan 2, RP-2.11).
// ---------------------------------------------------------------------------
//
// The exit desk's template, turned round: a scan narrows the list, a search
// narrows it further, one press opens a confirm under the row, a second press
// writes the record. The confirm says in one line exactly what will be written
// — who, what kind of visit, for which unit and host, on whose pass — because
// the point of the one tap is that the SERVER builds the whole arrival from the
// pass (POST /visitor-log/from-pass) and the desk should see that record before
// it exists.
//
// THE SCAN SELECTS. THE DESK SIGNS IN. A scanned document fills the search and
// may pick a row out; it never presses anything. Names are not unique.
//
// Reading this list is not work, so the screen opens off the clock; SIGNING
// somebody in or out is, so those actions wait for a clock-in and say so.
//
// THE SCREEN CLAIMS ONLY WHAT HAPPENED (RP-9.7).
//   * A scanned pass belongs to the ONE confirm it opened. When that confirm
//     closes — Cancel, another row, a search, an ID scan — the scan is over:
//     a later tap on the row is an ordinary tap (no "Scanned pass", the plain
//     route, an audit row without "via: link").
//   * The toast after a sign-in says how many were WRITTEN. The server clamps
//     the party to what the pass has left, and a tablet with a stale list can
//     ask for more than that.
//   * "Scan a pass" works on an empty list: the server answers for a code that
//     is not for today.

/* Layout animation (survivors sliding when a row leaves or moves) for a list
   that can be read at a glance; past this, rows still fade but stop measuring
   each other. The exit desk's cap, for the same reason. */
const LAYOUT_CAP = 60;

const SCAN_HINT =
  'Scan the visitor’s ID or the pass on their phone, or search the list, then confirm. A scan only finds their pass — nobody is signed in until you confirm.';

const NOT_TODAY_TEXT =
  'This pass is not valid right now, so it cannot sign them in. Sign them in as a visitor and ring up the unit.';

const SUBSCRIPTION_TEXT = 'Your building’s Lobbero subscription is inactive.';

// Lobbero Stays plan S3: the short-stay lane's answers.
const STAY_NOT_TODAY_TEXT =
  'Not expected today — sign them in as a normal visitor and ring the host.';
const STAY_SIGN_IN_FAILED_TEXT = 'Could not sign the guest in.';

// Plan 6 (RP-6.4): the answers to a scanned pass code.
const LINK_NOT_VALID_TEXT = 'This pass is no longer valid. Sign them in as a visitor and ring up the unit.';
const OTHER_BUILDING_TEXT = 'This pass is for another building.';
const NOT_A_PASS_TEXT = 'That code is not a Lobbero visitor pass. Search the list, or scan their ID.';
// A valid code, for a pass with no visit on today's list.
const PASS_NOT_TODAY_SCAN_TEXT =
  'This pass is not for today. If they are here, sign them in as a visitor and ring up the unit.';

/** "Maria signed in" — or, when fewer were written than asked for, how many. */
export const signedInToast = (first: string, asked: number, written: number | null): string =>
  written !== null && written >= 1 && written < asked
    ? `${first} signed in — ${written} of ${asked}. The pass allows no more right now.`
    : `${first} signed in`;

type Tone = 'brand' | 'warning' | 'success';

export const ExpectedPage: React.FC<DefaultComponentProps> = ({
  selectedBuilding,
  finish,
}) => {
  useDocumentTitle('Expected today');
  const navigate = useNavigate();
  const location = useLocation();

  const buildingId = Number(selectedBuilding?.id) || null;
  const { permissions, user } = useAuth();
  // The server gates the sign-in on visitorlog:create and the sign-out on the
  // same; checking here only decides whether to EXPLAIN instead of offering a
  // press that is going to 403.
  const canRecord = can(permissions, PERMISSIONS.VISITORLOG_CREATE);
  // THE CLOCK GATE — the same rule, from the same shared state, as the main
  // menu's recordsWork cards (main-menu.tsx). Only people who HAVE a clock are
  // asked to use it; null (the first read has not answered) keeps the actions
  // off without claiming a reason yet.
  const canClockShift = can(permissions, PERMISSIONS.SHIFT_CLOCK);
  const { isOnShiftAt } = useShifts();
  const onShift = buildingId !== null ? isOnShiftAt(buildingId) : null;
  const clockGateApplies = canClockShift && buildingId !== null;
  const blocked = !canRecord || (clockGateApplies && onShift !== true);
  const blockedReason = !canRecord
    ? 'Signing visitors in and out needs the visitor log permission.'
    : clockGateApplies && onShift === false
      ? 'Clock in to sign visitors in'
      : null;

  const expected = useExpectedToday(buildingId);
  const { refresh } = expected;
  const rows = useMemo(
    () => (expected.rows ? sortExpected(expected.rows) : null),
    [expected.rows]
  );
  // The building's zone as the SERVER resolved it for this list, falling back
  // to the building record until the first read answers.
  const zone = expected.deskDay?.zone || selectedBuilding?.timezone || null;
  const nowMs = Date.parse(expected.generatedAt || '') || Date.now();
  /* A desk screen: every time here is the BUILDING's, and says so — always,
     not only when the tablet happens to be set to another zone (R-T12). */
  const clock = useClock({ buildingId: selectedBuilding?.id, buildingZone: zone });

  // ── THE SHORT-STAY LANE (Lobbero Stays plan S3) ──────────────────────────
  // Read by whoever holds stays:desk. A concierge holds that but NOT
  // stays:read, so the organisation's entitlement read would answer them 403;
  // the lane's own `stays_enabled` is the live word for this building, and the
  // card shows only once it has said true. Independent of the pass switch: a
  // building with passes off still shows its short-stay guests.
  // Read only where the building's row says Stays is on, as the main menu does:
  // an organisation with Stays off answers every Stays route 404 (R-S1.4), so an
  // ungated read failed — and re-polled every 60 s — on every desk without Stays.
  const canDeskStays = can(permissions, PERMISSIONS.STAYS_DESK);
  // IS7: with the building-link switch on, a desk reads the lane even with Stays off
  // here: another account's linked guests arrive in it, and its answer is the live word.
  const buildingHasStays = selectedBuilding?.stays_enabled === true || user?.stays_building_link === true;
  const stays = useExpectedStays(buildingId, { enabled: canDeskStays && buildingHasStays });
  const { refresh: refreshStays } = stays;
  const stayRows = useMemo(
    () => (stays.rows ? sortExpectedStays(stays.rows) : null),
    [stays.rows]
  );
  const showStays = staysLaneVisible({
    canDeskStays,
    liveStaysEnabled: stays.staysEnabled,
  });
  const stayZone = stays.deskDay?.zone || zone;
  // A tick the server asked for that the list did not know about yet (the
  // window or the check-in moved on between the read and the tap).
  const [stayForced, setStayForced] = useState<
    Record<string, { outside?: boolean; incomplete?: boolean }>
  >({});

  const [query, setQuery] = useState('');
  // The row a scan or a deep link picked out, so it can be marked in the list.
  const [picked, setPicked] = useState<string | null>(null);
  // The desk's own pass writes (migration 118): offered only where the
  // building lets this desk add passes, and only while passes are on.
  const [deskPassesOpen, setDeskPassesOpen] = useState(false);
  // The pass behind a row, opened from its name (Plan 3 RP-3.5).
  const [openPassId, setOpenPassId] = useState<string | null>(null);
  const canAddPasses =
    !!buildingId && expected.passesEnabled === true && !!expected.passAccess?.can_add;
  // The ONE open confirm on the screen, and which act it is.
  const [confirming, setConfirming] = useState<{
    key: string;
    act: ConfirmAct;
  } | null>(null);
  /* THE SECOND TAP ON THE SAME CONFIRM. `pending` is state, so two presses
     inside one React commit both see it clear; this ref is read and written
     synchronously and is what actually refuses the duplicate. Keyed on the
     PASS for a sign-in (the server locks per pass) and on the ARRIVAL for a
     sign-out, so two different rows never block each other. */
  const inFlight = useRef<Set<string>>(new Set());
  const [pending, setPending] = useState<ReadonlySet<string>>(() => new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, RowInlineError>>({});
  const [outsideRequired, setOutsideRequired] = useState<ReadonlySet<string>>(
    () => new Set()
  );
  const [notice, setNotice] = useState<{ tone: Tone; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  /* A SCANNED PASS (Plan 6). The pass code a guest showed, kept for the one
     row it opened so that row's Confirm signs in through the token route —
     the same rules, and the server checks the link is still live at the
     moment of the tap. Kept in memory only, never shown, never logged. */
  const [scanned, setScanned] = useState<{ passId: string; token: string; key: string } | null>(
    null
  );

  /* A SCAN IS GOOD FOR THE CONFIRM IT OPENED AND NOTHING ELSE (QA Q-16). It
     used to be dropped at five named places and kept everywhere else, so after
     Cancel the code stayed on the row: the next plain tap showed "Scanned
     pass", signed in through the link route and audited "via: link" — for a
     scan the desk had cancelled. Derived from the open confirm instead: the
     moment it is anything but that row's sign-in (closed, another row, a
     search, an ID scan), the code is gone. */
  useEffect(() => {
    if (scanned && !(confirming && confirming.act === 'in' && confirming.key === scanned.key)) {
      setScanned(null);
    }
  }, [confirming, scanned]);

  const setRowError = (key: string, value: RowInlineError | null) =>
    setRowErrors(prev => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];

      return next;
    });

  const markPending = (gate: string, on: boolean) => {
    if (on) inFlight.current.add(gate);
    else inFlight.current.delete(gate);
    setPending(prev => {
      const next = new Set(prev);
      if (on) next.add(gate);
      else next.delete(gate);

      return next;
    });
  };

  const closeIf = (key: string, act: ConfirmAct) =>
    setConfirming(prev => (prev && prev.key === key && prev.act === act ? null : prev));

  const toggleKey = (key: string, act: ConfirmAct) => {
    setRowError(key, null);
    setConfirming(prev =>
      prev && prev.key === key && prev.act === act ? null : { key, act }
    );
  };
  const toggle = (row: ExpectedRow, act: ConfirmAct) => toggleKey(rowKey(row), act);

  // ── ?pass=<id> — OPEN THAT ROW'S CONFIRM (Plan 3's walk-in banner) ────────
  // Once per id, and only once the list AND the shift have answered: opening
  // before the clock is known would either offer a press the gate then takes
  // away or skip the panel for good.
  const passParam = new URLSearchParams(location.search).get('pass');
  const fromWalkIn = new URLSearchParams(location.search).get('from') === 'walk-in';
  const handledPass = useRef<string | null>(null);
  useEffect(() => {
    if (!passParam || !rows || handledPass.current === passParam) return;
    if (clockGateApplies && onShift === null) return;
    handledPass.current = passParam;
    const row =
      rows.find(r => r.pass_id === passParam && isSignable(r)) ||
      rows.find(r => r.pass_id === passParam);
    if (!row) {
      setNotice({
        tone: 'warning',
        text: 'That visitor pass is not on today’s list. If they are here, sign them in as a visitor and ring up the unit.',
      });

      return;
    }
    const key = rowKey(row);
    setQuery('');
    setPicked(key);
    if (isSignable(row) && !blocked) setConfirming({ key, act: 'in' });
    window.requestAnimationFrame(() => {
      document
        .getElementById(rowDomId(row))
        ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
    });
  }, [passParam, rows, blocked, clockGateApplies, onShift]);

  /* A SCANNED ID NARROWS BOTH LISTS — the passes and the short-stay guests —
     by name. The scanned name becomes the one search box both lists read. */
  const applyScan = (outcome: ExpectedScanOutcome, stayMatches: ExpectedStayRow[] = []) => {
    setConfirming(null);
    const passMatches =
      outcome.kind === 'none' ? 0 : outcome.kind === 'one' ? 1 : outcome.rows.length;
    const total = passMatches + stayMatches.length;
    if (outcome.kind === 'none' && stayMatches.length > 0) {
      setQuery(outcome.scanned);
      if (total === 1) {
        const match = stayMatches[0];
        setPicked(stayKey(match));
        setNotice({
          tone: 'brand',
          text: `Matched ${stayFullName(match)}. Check it is the right visitor, then sign them in.`,
        });
        window.requestAnimationFrame(() => {
          document
            .getElementById(stayDomId(match))
            ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
        });
      } else {
        setPicked(null);
        setNotice({
          tone: 'warning',
          text: `${total} people on today’s list match “${outcome.scanned}”. Pick the right one before signing in.`,
        });
      }

      return;
    }
    if (outcome.kind === 'none') {
      setPicked(null);
      setQuery('');
      setNotice({
        tone: 'warning',
        text: outcome.scanned
          ? `Nobody on today’s list matches “${outcome.scanned}”. If they have no pass, sign them in as a visitor and ring up the unit.`
          : 'The document was read but carried no name. Search the list below.',
      });

      return;
    }
    // The scanned name becomes the SEARCH, so the desk can see what was matched
    // and widen it by editing the box rather than by scanning again.
    setQuery(outcome.scanned);
    if (outcome.kind === 'one' && total === 1) {
      setPicked(rowKey(outcome.row));
      setNotice({
        tone: 'brand',
        text: `Matched ${fullNameOf(outcome.row)}. Check it is the right visitor, then sign them in.`,
      });

      return;
    }
    setPicked(null);
    setNotice({
      tone: 'warning',
      text: `${total} people on today’s list match “${outcome.scanned}”. Pick the right one before signing in.`,
    });
  };

  // ── SCAN A PASS (Plan 6, RP-6.4) — the guest's QR opens THEIR row's confirm ──
  // The camera read the code on the tablet; the token is mapped to a pass at
  // this building, and that row's Confirm opens exactly as for a tap (one
  // confirm UI on the desk), the scanned name large so the desk compares it
  // with the person. Nothing is signed in until the desk confirms.
  const applyPassScan = async (decoded: string) => {
    setConfirming(null);
    const token = tokenFromScan(decoded);
    if (!token || !buildingId) {
      setScanned(null);
      setNotice({ tone: 'warning', text: NOT_A_PASS_TEXT });

      return;
    }
    try {
      const { pass_id: passId } = await resolvePassLink(buildingId, token);
      const list = rows ?? [];
      const row =
        list.find(r => r.pass_id === passId && isSignable(r)) || list.find(r => r.pass_id === passId);
      if (!row) {
        setScanned(null);
        setNotice({ tone: 'warning', text: PASS_NOT_TODAY_SCAN_TEXT });

        return;
      }
      const key = rowKey(row);
      setQuery('');
      setPicked(key);
      setScanned({ passId, token, key });
      setNotice({
        tone: 'brand',
        text: `Pass scanned: ${fullNameOf(row)}. Check it is them, then confirm.`,
      });
      if (isSignable(row) && !blocked) setConfirming({ key, act: 'in' });
      window.requestAnimationFrame(() => {
        document
          .getElementById(rowDomId(row))
          ?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      });
    } catch (e) {
      const err = e instanceof SignInFromPassError ? e : null;
      setScanned(null);
      setPicked(null);
      setNotice({
        tone: 'warning',
        text:
          err?.code === 'PASS_OTHER_BUILDING'
            ? OTHER_BUILDING_TEXT
            : err?.code === 'PASS_LINK_NOT_VALID' || err?.status === 404
              ? LINK_NOT_VALID_TEXT
              : err?.status === 400
                ? NOT_A_PASS_TEXT
                : 'Could not check that pass. Check the connection and try again.',
      });
    }
  };

  const submitSignIn = async (row: ExpectedRow, choice: SignInChoice) => {
    if (!buildingId || blocked) return;
    const key = rowKey(row);
    const gate = `in:${row.pass_id}`;
    if (inFlight.current.has(gate)) return;
    markPending(gate, true);
    setRowError(key, null);
    setError(null);
    const viaToken =
      scanned && scanned.passId === row.pass_id && scanned.key === key ? scanned.token : null;
    try {
      let result: SignInFromPassResult;
      if (viaToken) {
        result = await signInFromPassToken({
          token: viaToken,
          buildingId,
          numberOfGuests: choice.guests,
          notes: choice.note.trim() || undefined,
          confirmOutsideWindow: choice.confirmOutsideWindow,
        });
        setScanned(null);
      } else {
        result = await signInFromPass({
          passId: row.pass_id,
          buildingId,
          numberOfGuests: choice.guests,
          notes: choice.note.trim() || undefined,
          confirmOutsideWindow: choice.confirmOutsideWindow,
        });
      }
      // What was WRITTEN, not what was asked: the server clamps the party to
      // what the pass has left (QA Q-20).
      const written = Number(result?.numberOfGuests);
      toast.success(
        signedInToast(
          firstNameOf(row),
          choice.guests,
          Number.isInteger(written) && written > 0 ? written : null
        )
      );
      // Arrived from the wizard's walk-in banner (RP-3.9): the visitor is signed
      // in, so the half-typed wizard entry for them is moot. `finish` is the
      // visitor wizard's own reset (the router hands this standalone route the
      // visitor flow's): it empties the in-memory entry AND drops the
      // sessionStorage draft, so the next Visitor In starts clean. Only for THIS
      // pass, only on success.
      if (fromWalkIn && passParam === row.pass_id && finish) finish();
      setPicked(null);
      // Refresh FIRST, then close: the panel closes onto the row as it now is
      // (Inside, with its Sign out), and focus has somewhere real to land.
      await refresh();
      closeIf(key, 'in');
    } catch (e) {
      const err = e instanceof SignInFromPassError ? e : null;
      const code = err?.code ?? null;
      const status = err?.status ?? null;

      if (code === 'PASS_LINK_NOT_VALID' || code === 'PASS_OTHER_BUILDING') {
        // The shared link stopped working between the scan and the tap (the
        // household stopped sharing, the pass ended).
        setScanned(null);
        closeIf(key, 'in');
        setNotice({
          tone: 'warning',
          text: code === 'PASS_OTHER_BUILDING' ? OTHER_BUILDING_TEXT : LINK_NOT_VALID_TEXT,
        });
        await refresh();
      } else if (code === 'ALREADY_INSIDE') {
        // Another tablet (or this one, a moment ago) got there first.
        toast('Already signed in');
        await refresh();
        closeIf(key, 'in');
      } else if (code === 'PASS_NOT_TODAY') {
        setRowError(key, { kind: 'not_today', text: NOT_TODAY_TEXT });
        await refresh();
      } else if (code === 'PASS_OUTSIDE_WINDOW') {
        // The window moved on since the list was read. Ask, explicitly.
        setOutsideRequired(prev => new Set(prev).add(key));
        setRowError(key, {
          kind: 'text',
          text: 'It is now outside the pass window. Tick the line above to sign them in anyway.',
        });
        await refresh();
      } else if (code === 'PASSES_DISABLED') {
        closeIf(key, 'in');
        setError('Visitor passes are switched off for this building. Nothing was signed in.');
        await refresh();
      } else if (code === 'PASS_NOT_FOUND' || status === 404) {
        closeIf(key, 'in');
        setNotice({
          tone: 'warning',
          text: 'That pass is no longer on today’s list. Nothing was signed in.',
        });
        await refresh();
      } else if (status === 402 || code === 'SUBSCRIPTION_ENDED') {
        setRowError(key, { kind: 'text', text: SUBSCRIPTION_TEXT });
        await refresh();
      } else if (status === 403) {
        setRowError(key, {
          kind: 'text',
          text: 'You do not have permission to sign visitors in.',
        });
        await refresh();
      } else {
        setRowError(key, {
          kind: 'text',
          text: 'Could not sign the visitor in. Check the connection and try again.',
        });
        await refresh();
      }
    } finally {
      markPending(gate, false);
    }
  };

  const submitSignOut = async (row: ExpectedRow) => {
    if (row.arrival_id == null || blocked) return;
    const key = rowKey(row);
    const gate = `out:${row.arrival_id}`;
    if (inFlight.current.has(gate)) return;
    markPending(gate, true);
    setRowError(key, null);
    setError(null);
    try {
      await signVisitorOut(row.arrival_id);
      toast.success(`${firstNameOf(row)} signed out`);
      setPicked(null);
      await refresh();
      closeIf(key, 'out');
    } catch (e) {
      const err = e instanceof VisitorExitError ? e : null;
      if (err?.code === 'ALREADY_DEPARTED') {
        toast('Already signed out');
        await refresh();
        closeIf(key, 'out');
      } else {
        setRowError(key, {
          kind: 'text',
          text: 'Could not record the departure. Check the connection and try again.',
        });
        await refresh();
      }
    } finally {
      markPending(gate, false);
    }
  };

  // ── THE SHORT-STAY ONE TAP (plan S3) — POST /stays/desk/…/sign-in ─────────
  // The pass sign-in's discipline: one gate per stay (`stay:<id>`), the clock
  // gate, refresh FIRST and then close, and every refusal said on the row.
  const forceStay = (key: string, which: 'outside' | 'incomplete') =>
    setStayForced(prev => ({ ...prev, [key]: { ...prev[key], [which]: true } }));

  const submitStaySignIn = async (row: ExpectedStayRow, choice: StaySignInChoice) => {
    if (!buildingId || blocked) return;
    const key = stayKey(row);
    const gate = staySignInGate(row);
    if (inFlight.current.has(gate)) return;
    markPending(gate, true);
    setRowError(key, null);
    setError(null);
    try {
      const note = choice.note.trim();
      await signInFromStay(row.reservation_id, {
        building_id: buildingId,
        number_of_guests: choice.guests,
        ...(note ? { notes: note } : {}),
        confirm_outside_window: choice.confirmOutsideWindow,
        confirm_incomplete: choice.confirmIncomplete,
      });
      toast.success(`${stayFirstName(row)} is signed in.`);
      setPicked(null);
      setStayForced(prev => {
        const next = { ...prev };
        delete next[key];

        return next;
      });
      await refreshStays();
      closeIf(key, 'in');
    } catch (e) {
      const err = e instanceof SignInFromStayError ? e : null;
      const code = err?.code ?? null;
      if (code === 'STAY_NOT_TODAY') {
        setRowError(key, { kind: 'not_today', text: STAY_NOT_TODAY_TEXT });
      } else if (code === 'ALREADY_SIGNED_IN') {
        setRowError(key, { kind: 'text', text: 'Already signed in.' });
      } else {
        // The window or the check-in moved on since the list was read: the
        // tick it now needs appears, and the server's words say why.
        if (code === 'STAY_OUTSIDE_WINDOW') forceStay(key, 'outside');
        if (code === 'STAY_CHECKIN_INCOMPLETE') forceStay(key, 'incomplete');
        setRowError(key, {
          kind: 'text',
          text: (err && err.message) || STAY_SIGN_IN_FAILED_TEXT,
        });
      }
      await refreshStays();
    } finally {
      markPending(gate, false);
    }
  };

  const submitStaySignOut = async (row: ExpectedStayRow) => {
    if (row.arrival_id == null || blocked) return;
    const key = stayKey(row);
    const gate = `out:${row.arrival_id}`;
    if (inFlight.current.has(gate)) return;
    markPending(gate, true);
    setRowError(key, null);
    setError(null);
    try {
      await signVisitorOut(row.arrival_id);
      toast.success(`${stayFirstName(row)} signed out`);
      setPicked(null);
      await refreshStays();
      closeIf(key, 'out');
    } catch (e) {
      const err = e instanceof VisitorExitError ? e : null;
      if (err?.code === 'ALREADY_DEPARTED') {
        toast('Already signed out');
        await refreshStays();
        closeIf(key, 'out');
      } else {
        setRowError(key, {
          kind: 'text',
          text: 'Could not record the departure. Check the connection and try again.',
        });
        await refreshStays();
      }
    } finally {
      markPending(gate, false);
    }
  };

  const visibleStays = stayRows ? filterExpectedStays(stayRows, query) : [];
  const staysState =
    stayRows === null
      ? 'loading'
      : stayRows.length === 0
        ? 'empty'
        : visibleStays.length === 0
          ? 'nomatch'
          : 'ready';
  const staysCounts = stays.counts || { expected: 0, inside: 0, incomplete: 0, signable_now: 0 };

  const renderStays = () => {
    if (staysState === 'loading') {
      return <ListSkeleton rows={2} label="today’s short-stay guests" />;
    }
    if (staysState === 'empty') {
      return <Empty>No short-stay guests are expected today.</Empty>;
    }
    if (staysState === 'nomatch') {
      return (
        <Empty>
          No short-stay guest matches “{query}”.
          <RowAction type="button" variant="quiet" onClick={() => setQuery('')}>
            Clear search
          </RowAction>
        </Empty>
      );
    }

    return (
      <Staleable stale={stays.refreshing}>
        <Presence>
          {visibleStays.map((row, index) => {
            const key = stayKey(row);
            const open = confirming && confirming.key === key ? confirming.act : null;

            return (
              <ExpectedStayRowItem
                key={key}
                row={row}
                index={index}
                layout={visibleStays.length <= LAYOUT_CAP}
                zone={stayZone}
                tone={picked === key ? 'picked' : 'plain'}
                open={open}
                onToggle={act => toggleKey(key, act)}
                onClose={() => setConfirming(null)}
                blocked={blocked}
                blockedReason={blockedReason}
                pendingIn={pending.has(staySignInGate(row))}
                pendingOut={row.arrival_id != null && pending.has(`out:${row.arrival_id}`)}
                forceOutside={!!stayForced[key]?.outside}
                forceIncomplete={!!stayForced[key]?.incomplete}
                inlineError={rowErrors[key] || null}
                onSignIn={choice => {
                  void submitStaySignIn(row, choice);
                }}
                onSignOut={() => {
                  void submitStaySignOut(row);
                }}
                onSignInAsVisitor={() => navigate('/visitor-log')}
              />
            );
          })}
        </Presence>
      </Staleable>
    );
  };

  const visible = rows ? filterExpected(rows, query) : [];

  // ONE WORD FOR WHAT THE LIST IS, keying the crossfade below. Loading, failed
  // and empty are three different renders; a refresh keeps the rows and dims.
  const listState =
    expected.error === 'denied'
      ? 'denied'
      : expected.passesEnabled === false
        ? 'off'
        : rows === null
          ? expected.error
            ? 'failed'
            : 'loading'
          : rows.length === 0
            ? 'empty'
            : visible.length === 0
              ? 'nomatch'
              : 'ready';

  const all = rows || [];
  const stillExpected = all.filter(r => r.arrival_state === 'not_arrived').length;
  const insideCount = all.filter(r => r.arrival_state === 'inside').length;
  const lateCount = all.filter(
    r => r.arrival_state === 'not_arrived' && r.window_state === 'late'
  ).length;

  const renderList = () => {
    if (listState === 'loading') {
      return <ListSkeleton rows={3} label="today’s expected visitors" />;
    }
    if (listState === 'failed') {
      return (
        <LoadError
          message="Could not load today’s expected visitors."
          onRetry={() => {
            void refresh();
          }}
        />
      );
    }
    if (listState === 'denied') {
      return (
        <Empty>
          You cannot see today’s expected visitors — that needs the visitor pass
          permission. Ask an owner or property manager on your team.
        </Empty>
      );
    }
    if (listState === 'off') {
      return (
        <Empty>
          Visitor passes are switched off for this building, so nobody is
          expected on a pass. Sign visitors in from the main menu as usual.
        </Empty>
      );
    }
    if (listState === 'empty') {
      return <Empty>Nobody is expected on a visitor pass today.</Empty>;
    }
    if (listState === 'nomatch') {
      return (
        <Empty>
          Nobody on today’s list matches “{query}”. If they have no pass, sign
          them in as a visitor.
          <RowAction type="button" variant="quiet" onClick={() => setQuery('')}>
            Clear search
          </RowAction>
        </Empty>
      );
    }

    return (
      <Staleable stale={expected.refreshing}>
        <Presence>
          {visible.map((row, index) => {
            const key = rowKey(row);
            const open = confirming && confirming.key === key ? confirming.act : null;

            return (
              <ExpectedRowItem
                key={key}
                row={row}
                index={index}
                layout={visible.length <= LAYOUT_CAP}
                zone={zone}
                nowMs={nowMs}
                tone={picked === key ? 'picked' : 'plain'}
                open={open}
                onToggle={act => toggle(row, act)}
                onClose={() => setConfirming(null)}
                blocked={blocked}
                blockedReason={blockedReason}
                pendingIn={pending.has(`in:${row.pass_id}`)}
                pendingOut={row.arrival_id != null && pending.has(`out:${row.arrival_id}`)}
                outsideRequired={outsideRequired.has(key)}
                inlineError={rowErrors[key] || null}
                scannedName={scanned && scanned.passId === row.pass_id ? fullNameOf(row) : null}
                onSignIn={choice => {
                  void submitSignIn(row, choice);
                }}
                onSignOut={() => {
                  void submitSignOut(row);
                }}
                onSignInAsVisitor={() => navigate('/visitor-log')}
                onOpenPass={setOpenPassId}
              />
            );
          })}
        </Presence>
      </Staleable>
    );
  };

  // Narrowed vs total, shown only when they differ.
  const narrowed = query.trim() ? visible.length : null;
  const subtitle = [
    selectedBuilding?.property_name || 'No building selected',
    deskDateLabel(expected.deskDay?.date),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <NavbarAndContentContainer>
      <ContentAndButtonsContainer>
        <ExpectedBody>
          <ScreenHeader
            title="Expected today"
            subtitle={subtitle}
            actions={
              <AppButton variant="ghost" onClick={() => navigate('/visitor-log/passes')}>
                All visitor passes
              </AppButton>
            }
          />
          <ZoneNote
            data-clock-note=""
            data-clock-whose={clock.whose}
            data-clock-zone={clock.zone}
          >
            {clockNote(clock)}
          </ZoneNote>

          <Presence>
            {error ? (
              <Banner key="error" tone="alert" role="alert" {...bannerMotion}>
                {error}
              </Banner>
            ) : null}
            {expected.error === 'failed' && rows !== null ? (
              <Banner key="stale" tone="warning" role="status" {...bannerMotion}>
                Could not refresh today’s list. What is shown may be out of date.
              </Banner>
            ) : null}
            {notice ? (
              <Banner key="notice" tone={notice.tone} role="status" {...bannerMotion}>
                {notice.text}
              </Banner>
            ) : null}
          </Presence>

          {/* ---------- THE SCAN ---------- */}
          <Card>
            <CardTitle>Who has arrived?</CardTitle>
            <Hint>{SCAN_HINT}</Hint>
            <ScanRow>
              <ScanIdButton
                mode="id"
                buildingId={buildingId}
                label="Scan ID"
                disabled={
                  !buildingId ||
                  ((!rows || rows.length === 0) &&
                    !(showStays && stayRows !== null && stayRows.length > 0))
                }
                onResult={r => {
                  // IT FILLS THE SEARCH AND STORES NOTHING: no image, no
                  // extracted fields. The arrival, when the desk confirms it,
                  // is built from the pass (or the stay). Both lists narrow.
                  applyScan(
                    matchScannedPass(rows ?? [], r.first_name, r.last_name),
                    showStays ? matchScannedStays(stayRows ?? [], r.first_name, r.last_name) : []
                  );
                }}
              />
              {/* Plan 6: the QR on a guest's phone. Read ON THE TABLET — no
                  upload, no model — then mapped to their row. */}
              {/* Enabled on an EMPTY list too: a guest can hold a code for
                  a pass that is not for today, and the server's answer to the
                  scan is what tells the desk so. */}
              <ScanIdButton
                mode="pass"
                buildingId={buildingId}
                label="Scan a pass"
                disabled={!buildingId || !rows}
                onPass={decoded => {
                  void applyPassScan(decoded);
                }}
              />
            </ScanRow>
          </Card>

          {/* ---------- SHORT-STAY GUESTS (Lobbero Stays plan S3) ----------
              Only once the lane has said Stays is on here; it does not
              depend on the pass switch. The search box below filters it too. */}
          {showStays ? (
            <Card data-lane="short-stays">
              <CardTitle>Short-stay guests</CardTitle>
              <Stats>
                <Stat tone="plain">
                  <b>{staysCounts.expected}</b>
                  <span>Expected</span>
                </Stat>
                <Stat tone={staysCounts.inside > 0 ? 'brand' : 'plain'}>
                  <b>{staysCounts.inside}</b>
                  <span>Inside</span>
                </Stat>
                <Stat tone={staysCounts.incomplete > 0 ? 'warning' : 'plain'}>
                  <b>{staysCounts.incomplete}</b>
                  <span>Incomplete</span>
                </Stat>
              </Stats>
              {stays.error === 'failed' ? (
                <Hint role="status">
                  Could not refresh the short-stay guests. What is shown may be out of date.
                </Hint>
              ) : null}
              <StateSwitch state={staysState} minHeight={96}>
                {renderStays()}
              </StateSwitch>
            </Card>
          ) : null}

          {/* ---------- TODAY'S LIST ---------- */}
          <Card>
            <ListHead>
              <CardTitle>Today’s list</CardTitle>
              {canAddPasses ? (
                <AppButton variant="secondary" onClick={() => setDeskPassesOpen(true)}>
                  Add expected visitor
                </AppButton>
              ) : null}
            </ListHead>
            <Stats>
              <Stat>
                <b>{stillExpected}</b>
                <span>Expected</span>
              </Stat>
              <Stat tone={insideCount > 0 ? 'brand' : 'plain'}>
                <b>{insideCount}</b>
                <span>Inside</span>
              </Stat>
              <Stat tone={lateCount > 0 ? 'warning' : 'plain'}>
                <b>{lateCount}</b>
                <span>Late</span>
              </Stat>
            </Stats>
            <SearchRow>
              <Label htmlFor="expected-search">Search</Label>
              <Input
                id="expected-search"
                value={query}
                placeholder="Name, unit or company"
                onChange={e => {
                  setQuery(e.target.value);
                  setPicked(null);
                  setConfirming(null);
                }}
              />
            </SearchRow>
            {narrowed !== null ? (
              <Hint role="status">
                {`Showing ${narrowed} of ${all.length}`}
              </Hint>
            ) : null}
            <StateSwitch state={listState} minHeight={192}>
              {renderList()}
            </StateSwitch>
          </Card>
        </ExpectedBody>
      </ContentAndButtonsContainer>
      {buildingId ? (
        <DeskPassesModal
          open={deskPassesOpen}
          buildingId={buildingId}
          onceOnly={!!expected.passAccess?.once_only}
          onClose={() => {
            setDeskPassesOpen(false);
            // A pass added for today belongs on this list now, not at the
            // next poll.
            void refresh();
          }}
        />
      ) : null}
      <PassDetailModal
        passId={openPassId}
        open={openPassId !== null}
        onClose={() => setOpenPassId(null)}
        // Edited, paused or ended from the modal: today's list follows now.
        onChanged={() => {
          void refresh();
        }}
      />
    </NavbarAndContentContainer>
  );
};
